import React, { useEffect, useState, memo } from "react";
import {
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import {
  useDetailQuery,
  useCreateMutation,
  useUpdateMutation,
} from "helpers/http-client/hooks/useCrud";
import { API_ENDPOINTS } from "helpers/http-client/httpConstants";

interface ActiveIngredientFormProps {
  ingredientId?: string;
  onSuccess: (ingredient: any) => void;
}

export interface ActiveIngredientFormValues {
  name: string;
  description?: string;
}

const ActiveIngredientForm: React.FC<ActiveIngredientFormProps> = ({
  ingredientId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const isEdit = Boolean(ingredientId);
  const url = isEdit ? API_ENDPOINTS.ACTIVE_INGREDIENTS : "";

  const { data, isLoading } = useDetailQuery<ActiveIngredientFormValues>(url, ingredientId);

  const [formValues, setFormValues] = useState<ActiveIngredientFormValues>({
    name: "",
    description: "",
  });

  const { mutate: createIngredient } = useCreateMutation(API_ENDPOINTS.ACTIVE_INGREDIENTS);
  const { mutate: updateIngredient } = useUpdateMutation(API_ENDPOINTS.ACTIVE_INGREDIENTS);

  useEffect(() => {
    if (data && isEdit) {
      setFormValues({
        name: data.name || "",
        description: data.description || "",
      });
    }
  }, [data, isEdit]);

  const handleChange = (field: keyof ActiveIngredientFormValues, value: string) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    const payload: ActiveIngredientFormValues = {
      name: formValues.name.trim(),
      description: formValues.description?.trim(),
    };

    if (isEdit && ingredientId) {
      updateIngredient({ id: ingredientId, data: payload }, { onSuccess });
    } else {
      createIngredient(payload, { onSuccess });
    }
  };

  if (isEdit && isLoading) {
    return <Spinner color="primary" />;
  }

  return (
    <>
      <FormGroup>
        <Label>
          {t("Active Ingredient Name")}{" "}
          <span
            className="text-primary"
            style={{ cursor: "help" }}
            title="Enter the name of the active ingredient. Example: Paracetamol, Ibuprofen"
          >
            ?
          </span>
        </Label>
        <Input
          value={formValues.name}
          placeholder={t("e.g., Paracetamol")}
          onChange={(e) => handleChange("name", e.target.value)}
        />
      </FormGroup>

      <FormGroup>
        <Label>
          {t("Description")}{" "}
          <span
            className="text-primary"
            style={{ cursor: "help" }}
            title="Provide a brief description of the ingredient if necessary."
          >
            ?
          </span>
        </Label>
        <Input
          type="textarea"
          value={formValues.description}
          placeholder={t("Optional detailed description")}
          onChange={(e) => handleChange("description", e.target.value)}
        />
      </FormGroup>

      <div className="d-flex justify-content-end">
        <Button color="primary" onClick={handleSubmit}>
          {isEdit ? t("Update") : t("Save")}
        </Button>
      </div>
    </>
  );
};

export default memo(ActiveIngredientForm);
